// AdminAppSubscriptionsPage — Phase 3 of unified back-office.
// Consumes /api/admin/app-bridge/subscriptions/*
import { useCallback, useEffect, useState } from "react";
import {
  CreditCard, TrendingUp, TrendingDown, Users, RefreshCw, Loader2, AlertCircle,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const AdminAppSubscriptionsPage = () => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, list] = await Promise.all([
        adminApi.appSubscriptionsStats().catch(() => null),
        adminApi.listAppSubscriptions({ plan, status, limit: 200 }),
      ]);
      setStats(s);
      setItems(Array.isArray(list) ? list : list?.items || list?.subscriptions || []);
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [plan, status]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const mrr = stats?.revenue?.mrr_eur ?? stats?.mrr_eur ?? 0;
  const arr = stats?.revenue?.arr_eur ?? stats?.arr_eur ?? mrr * 12;
  const activeTotal = (stats?.counts?.amateur_active ?? 0) + (stats?.counts?.pro_active ?? 0);
  const newPaidMonth = stats?.growth?.new_paid_month ?? 0;
  const churn = stats?.growth?.churn_pct ?? 0;
  const conversion = stats?.growth?.conversion_pct ?? 0;

  return (
    <div data-testid="admin-app-subscriptions">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Abonnements app.geovinum.io</h1>
        <button
          onClick={fetchAll}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px] hover:bg-[color:var(--gv-mist)] transition-colors"
          data-testid="app-subs-refresh-btn"
        >
          <RefreshCw size={13} /> Rafraîchir
        </button>
      </div>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-6">MRR, conversion, churn — pilotage financier centralisé.</p>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6" data-testid="app-subs-stats">
        <StatCard icon={CreditCard} label="MRR" value={`${mrr.toFixed(2)} €`} hint={`ARR ${arr.toFixed(0)} €`} testid="app-subs-mrr" />
        <StatCard icon={Users} label="Abonnés actifs" value={activeTotal} hint={`${stats?.counts?.amateur_active ?? 0} Amateur · ${stats?.counts?.pro_active ?? 0} Pro`} testid="app-subs-active" />
        <StatCard icon={TrendingUp} label="Nouveaux 30j" value={newPaidMonth} hint={`Conversion ${conversion.toFixed(1)}%`} testid="app-subs-new" />
        <StatCard icon={TrendingDown} label="Churn" value={`${churn.toFixed(1)}%`} hint="30 derniers jours" testid="app-subs-churn" highlight={churn > 5} />
      </div>

      {/* Filters */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-3 mb-5 flex flex-wrap gap-2 items-center" data-testid="app-subs-filters">
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className="gv-input" data-testid="app-subs-plan-filter">
          <option value="">Tous plans</option>
          <option value="amateur">Amateur</option>
          <option value="pro">Pro</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="gv-input" data-testid="app-subs-status-filter">
          <option value="">Tous statuts</option>
          <option value="active">Actifs</option>
          <option value="cancelled">Annulés</option>
        </select>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2 mb-4" data-testid="app-subs-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium mb-0.5">Impossible de récupérer les abonnements</div>
            <div className="text-[12px] opacity-70 mt-1">Vérifiez que l'app.geovinum.io backend est en ligne.</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="app-subs-loading">
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        ) : error ? null : items.length === 0 ? (
          <div className="p-8 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="app-subs-empty">
            <CreditCard size={14} /> Aucun abonnement trouvé
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--gv-mist)] text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Email</th>
                <th className="text-left px-4 py-2.5 font-medium">Nom</th>
                <th className="text-left px-4 py-2.5 font-medium">Plan</th>
                <th className="text-left px-4 py-2.5 font-medium">Début</th>
                <th className="text-left px-4 py-2.5 font-medium">Fin période</th>
                <th className="text-left px-4 py-2.5 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id || s._id || s.user_id || s.email} className="border-t border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]/30" data-testid={`app-sub-row-${s.id || s.email}`}>
                  <td className="px-4 py-2.5 text-[color:var(--gv-ink)]">{s.email}</td>
                  <td className="px-4 py-2.5 text-[color:var(--gv-ink)]/75">{s.name || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-[4px] text-[11px] font-medium ${
                      s.plan === "pro" ? "bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]" : "bg-blue-50 text-blue-700"
                    }`}>{s.plan}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[color:var(--gv-ink)]/70 text-[12px]">{fmtDate(s.plan_started_at)}</td>
                  <td className="px-4 py-2.5 text-[color:var(--gv-ink)]/70 text-[12px]">{fmtDate(s.subscription_period_end)}</td>
                  <td className="px-4 py-2.5">
                    {s.subscription_active ? (
                      <span className="text-[11.5px] text-emerald-600">● Actif</span>
                    ) : (
                      <span className="text-[11.5px] text-gray-500">○ Annulé</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="text-[11.5px] text-[color:var(--gv-ink)]/50 mt-2">{items.length} abonnement{items.length > 1 ? "s" : ""} affiché{items.length > 1 ? "s" : ""}</div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, hint, testid, highlight = false }) => (
  <div className={`bg-white border rounded-[5px] p-5 ${highlight ? "border-red-300" : "border-[color:var(--gv-line)]"}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} className={highlight ? "text-red-500" : "text-[color:var(--gv-ink)]/50"} />
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">{label}</div>
    </div>
    <div className="font-display text-3xl text-[color:var(--gv-ink)] mb-1">{value}</div>
    {hint && <div className="text-[11.5px] text-[color:var(--gv-ink)]/55">{hint}</div>}
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
};

export default AdminAppSubscriptionsPage;
